/**
 * Frontend validation for spreadsheet cells based on backend column types (Python typing).
 * Maps Python base types and common semantic types to validators; invalid rows/cells
 * are highlighted and errors shown via tooltip.
 */

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

/** Normalize backend col_type string to a canonical type for validation. Exported for use in Univer cell type mapping. */
export function normalizePythonType(colType: string | undefined): string {
  if (!colType || typeof colType !== 'string') return 'str';
  const t = colType.trim().toLowerCase();
  // Optional[X] -> X
  const optionalMatch = t.match(/^optional\[(.+)\]$/);
  if (optionalMatch) return normalizePythonType(optionalMatch[1]);
  // Union with None
  if (t.includes('|') && t.includes('none')) {
    const main = t
      .split('|')
      .find((x) => !x.includes('none'))
      ?.trim();
    return main ? normalizePythonType(main) : 'str';
  }
  // Common aliases
  if (['string', 'str', 'text'].includes(t)) return 'str';
  if (['int', 'integer'].includes(t)) return 'int';
  if (['float', 'number'].includes(t)) return 'float';
  if (['bool', 'boolean'].includes(t)) return 'bool';
  if (['url', 'website', 'website_url', 'domain', 'uri', 'link'].includes(t))
    return 'url';
  if (['email'].includes(t)) return 'email';
  if (['list'].includes(t)) return 'list';
  return t;
}

export function forceBooleanTrueFalse(value: string): 'TRUE' | 'FALSE' | null {
  const lower = value.trim().toLowerCase();
  if (['true', '1', 'yes'].includes(lower)) return 'TRUE';
  if (['false', '0', 'no'].includes(lower)) return 'FALSE';
  return null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Parse Python-style quoted list literal (single/double quoted strings, supports \' and \"). Returns null if invalid.
 */
function parsePythonListLiteral(s: string): string[] | null {
  const trimmed = s.trim();
  if (
    trimmed.length < 2 ||
    trimmed[0] !== '[' ||
    trimmed[trimmed.length - 1] !== ']'
  )
    return null;
  const inner = trimmed.slice(1, -1).trim();
  if (inner === '') return [];
  const items: string[] = [];
  let i = 0;
  while (i < inner.length) {
    while (i < inner.length && /[\s,]/.test(inner[i])) i++;
    if (i >= inner.length) break;
    const quote = inner[i];
    if (quote !== "'" && quote !== '"') return null;
    i++;
    let item = '';
    while (i < inner.length) {
      const ch = inner[i];
      if (ch === '\\') {
        i++;
        if (i < inner.length) {
          const next = inner[i];
          item += next === 'n' ? '\n' : next === 't' ? '\t' : next;
          i++;
        } else item += '\\';
      } else if (ch === quote) {
        i++;
        break;
      } else {
        item += ch;
        i++;
      }
    }
    items.push(item);
  }
  return items;
}

/**
 * Parse list input in any supported form: "item1, item2", "[item1, item2]", "['item1', 'item2']", or JSON ["item1", "item2"].
 * Returns array of strings, or null if not valid list input.
 */
function parseListInput(value: string): string[] | null {
  const trimmed = value.trim();
  if (trimmed === '') return [];

  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) return parsed.map((x) => String(x ?? ''));
    } catch {
      // not JSON
    }
    const pythonParsed = parsePythonListLiteral(trimmed);
    if (pythonParsed !== null) return pythonParsed;
    const inner = trimmed.slice(1, -1).trim();
    const parts = inner
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return parts;
  }

  const parts = trimmed
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : null;
}

/**
 * Valid list: empty, or parseListInput returns an array. Reject free text with " | " when not bracket format.
 */
function isValidListValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if (!trimmed.startsWith('[') && trimmed.includes(' | ')) return false;
  return parseListInput(trimmed) !== null;
}

/** Serialize string items to Python list format for the backend. */
function serializeToPythonList(items: string[]): string {
  const escaped = items.map(
    (s) => "'" + s.replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'"
  );
  return '[' + escaped.join(', ') + ']';
}

/**
 * Normalize list cell value to Python format for the backend: ['item1', 'item2'].
 * Accepts "item1, item2", "[item1, item2]", "['item1', 'item2']", or JSON ["item1", "item2"].
 */
export function normalizeListValueForBackend(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '[]';
  const items = parseListInput(trimmed);
  if (items === null) return trimmed;
  return serializeToPythonList(items);
}

/** Display form for list values in the UI: "item1, item2". If value is not parseable as list, returns as-is. */
export function formatListValueForDisplay(value: string): string {
  if (value == null || String(value).trim() === '') return '';
  const items = parseListInput(String(value).trim());
  if (items === null) return String(value);
  return items.join(', ');
}

/** Hostname must be localhost or contain at least one dot (TLD), e.g. example.com */
function hasValidHostname(hostname: string): boolean {
  if (!hostname) return false;
  const lower = hostname.toLowerCase();
  if (lower === 'localhost') return true;
  return lower.includes('.');
}

function validateByType(
  value: string,
  normalizedType: string
): ValidationResult {
  const trimmed = value.trim();
  // Empty: valid for optional types; for required we treat as valid and let backend decide
  if (trimmed === '') {
    return { valid: true };
  }

  switch (normalizedType) {
    case 'str':
      return { valid: true };

    case 'int': {
      const n = Number(trimmed);
      if (Number.isNaN(n) || !Number.isInteger(n)) {
        return { valid: false, message: 'Must be a whole number (integer).' };
      }
      return { valid: true };
    }

    case 'float':
    case 'num':
    case 'number': {
      const n = Number(trimmed);
      if (trimmed !== '' && Number.isNaN(n)) {
        return { valid: false, message: 'Must be a number.' };
      }
      return { valid: true };
    }

    case 'bool': {
      const lower = trimmed.toLowerCase();
      const allowed = ['true', 'false', '1', '0', 'yes', 'no'];
      if (!allowed.includes(lower)) {
        return { valid: false, message: 'Must be true/false, yes/no, or 1/0.' };
      }
      return { valid: true };
    }

    case 'url': {
      // Allow domain-only (e.g. "example.com") or full URL; require a TLD (dot in hostname) or localhost
      const withProtocol = trimmed.includes('://')
        ? trimmed
        : `https://${trimmed}`;
      try {
        const u = new URL(withProtocol);
        if (!hasValidHostname(u.hostname)) {
          return {
            valid: false,
            message: 'Enter a valid website URL or domain (e.g. example.com).'
          };
        }
      } catch {
        return {
          valid: false,
          message: 'Enter a valid website URL or domain (e.g. example.com).'
        };
      }
      return { valid: true };
    }

    case 'email': {
      if (!EMAIL_PATTERN.test(trimmed)) {
        return { valid: false, message: 'Enter a valid email address.' };
      }
      return { valid: true };
    }

    case 'date': {
      const date = new Date(trimmed);
      if (Number.isNaN(date.getTime())) {
        return { valid: false, message: 'Enter a valid date.' };
      }
      return { valid: true };
    }

    case 'list': {
      if (!isValidListValue(trimmed)) {
        return {
          valid: false,
          message: 'Enter a valid list (e.g. comma-separated values).'
        };
      }
      return { valid: true };
    }

    default:
      return { valid: true };
  }
}

/**
 * Validate a cell value against a column type (Python type string from backend).
 */
export function validateCell(
  value: string | number | boolean | null | undefined,
  colType: string | undefined
): ValidationResult {
  const str = value == null ? '' : String(value).trim();
  const normalized = normalizePythonType(colType);
  return validateByType(str, normalized);
}

export type CellValidationError = {
  row: number;
  col: number;
  columnName: string;
  value: string;
  message: string;
};

/**
 * Validate a grid of cell values by column.
 * columns: list of { col_name, col_type }; first column (index 0) is treated as domain/URL if name suggests it.
 * cellMatrix: row index -> col index -> raw value (string or number).
 * Returns list of errors (one per invalid cell).
 */
export function validateSheetCells(
  columns: { col_name: string; col_type?: string }[],
  cellMatrix: Record<number, Record<number, unknown>>
): CellValidationError[] {
  const errors: CellValidationError[] = [];
  const colCount = columns.length;

  for (const rowKey of Object.keys(cellMatrix)) {
    const row = Number(rowKey);
    const rowData = cellMatrix[row] as Record<number, unknown> | undefined;
    if (!rowData) continue;

    for (let col = 0; col < colCount; col++) {
      const colDef = columns[col];
      const colName = colDef?.col_name ?? `Column ${col}`;
      const colType = colDef?.col_type;
      const normalized = normalizePythonType(colType);
      const raw = rowData[col];
      const value = raw == null ? '' : String(raw);
      const result = validateCell(value, normalized);
      if (!result.valid && result.message) {
        errors.push({
          row,
          col,
          columnName: colName,
          value: value.slice(0, 50) + (value.length > 50 ? '…' : ''),
          message: result.message
        });
      }
    }
  }
  return errors;
}

/** Style to apply to invalid cells (red background). */
export const INVALID_CELL_STYLE = {
  bg: { rgb: '#ffcdd2' }
} as const;

/** Style to apply to cells currently being executed (enrichment in progress). Light blue to match app theme. */
export const ENRICHING_CELL_STYLE = {
  bg: { rgb: '#E8F4FC' }
} as const;

/** Style for valid cells; explicitly clears background so previous invalid styling is removed. */
export const VALID_CELL_STYLE = {
  bg: null
} as const;
